import { PeriodRangeDate, PeriodRangeString } from '../../../../store/types'
import { PagePaginationFromKeys, ServerResponse } from '../../../../types'
import { TraderProfitabilityTag } from '../../../../user/traderProfitability'
import { UserAccount, UserRolesEnum } from '../../../../user/types'
import { CashFlowEntry, CashFlowStatsResponseType, RawCashFlowEntry } from '../../../CashFlow'

export enum AccountColumnsEnum {
  name = 'name',
  platform = 'platform',
  currency = 'currency',
  balance = 'balance',
  login = 'login',
  server = 'server',
  manualBrokerName = 'manualBrokerName',
  createdAt = 'createdAt',
  userFirstName = 'userFirstName',
  userLastName = 'userLastName',
  userRole = 'userRole',
  userCountry = 'userCountry',
  userEmail = 'userEmail',
  userPhone = 'userPhone',
  userCreatedAt = 'userCreatedAt',
  credentialsValid = 'credentialsValid',
  importType = 'importType',
  profitabilityNetPnl30dUsd = 'profitabilityNetPnl30dUsd',
}

export type AccountColumnsType = Record<AccountColumnsEnum, string> & { actions: string }

export type AccountColumnsKeys = `${AccountColumnsEnum}`

export enum AccountFiltersEnum {
  excludeDemo = 'excludeDemo',
  excludeProp = 'excludeProp',
  onlyProp = 'onlyProp',
  excludeCfd = 'excludeCfd',
  onlyBreached = 'onlyBreached',
  excludeBreached = 'excludeBreached',
  onlyConfirmed = 'onlyConfirmed',
  breachStatusSuspected = 'breachStatusSuspected',
  breachStatusAssumed = 'breachStatusAssumed',
  breachStatusConfirmed = 'breachStatusConfirmed',
  breachReasonSyncInvalidCredentials = 'breachReasonSyncInvalidCredentials',
  breachReasonSyncRepeatedFailures = 'breachReasonSyncRepeatedFailures',
  breachReasonInactivity14d = 'breachReasonInactivity14d',
  breachReasonTradeDisabled = 'breachReasonTradeDisabled',
  breachReasonManual = 'breachReasonManual',
  platformMt4 = 'platformMt4',
  platformMt5 = 'platformMt5',
  platformGeneric = 'platformGeneric',
  breachedInProfit = 'breachedInProfit',
  breachedInLoss = 'breachedInLoss',
  onlyWithWithdrawal = 'onlyWithWithdrawal',
  excludeExcludedFromTop = 'excludeExcludedFromTop',
  onlyExcludedFromTop = 'onlyExcludedFromTop',
  profitabilityProfitable = 'profitabilityProfitable',
  profitabilityBreakeven = 'profitabilityBreakeven',
  profitabilityLosing = 'profitabilityLosing',
  profitabilityUnknown = 'profitabilityUnknown',
}

export const profitabilityAccountFilterByTag: Record<TraderProfitabilityTag, AccountFiltersEnum> = {
  [TraderProfitabilityTag.profitable]: AccountFiltersEnum.profitabilityProfitable,
  [TraderProfitabilityTag.breakeven]: AccountFiltersEnum.profitabilityBreakeven,
  [TraderProfitabilityTag.losing]: AccountFiltersEnum.profitabilityLosing,
  [TraderProfitabilityTag.unknown]: AccountFiltersEnum.profitabilityUnknown,
}

export const profitabilityAccountFilters = Object.values(profitabilityAccountFilterByTag)

export type AccountFiltersKeys = `${AccountFiltersEnum}`

export type AccountsPagination = PagePaginationFromKeys<
  AccountColumnsKeys,
  AccountFiltersKeys[]
> & {
  breachDetectedAtPeriod: PeriodRangeString
  breachEffectiveAtPeriod: PeriodRangeString
}

export type AccountUserType = {
  id: number
  firstName: string
  lastName: string
  role: UserRolesEnum
  country?: string
  email: string
  phone?: string
  createdAt: string
}

export type AccountType = Omit<UserAccount, 'password'> & {
  createdAt: string
  convertedBalance?: number
  user: AccountUserType
}

export type AccountsDataType = {
  data: AccountType[]
  meta: AccountsPagination
}

export type GetAccountsRequest = Pick<
  AccountsPagination,
  | 'page'
  | 'perPage'
  | 'columns'
  | 'order'
  | 'orderBy'
  | 'search'
  | 'filters'
  | 'breachDetectedAtPeriod'
  | 'breachEffectiveAtPeriod'
>
export type GetAccountsResponse = ServerResponse<AccountsDataType>
export type GetAccountsCSVResponse = Blob
export type GetCashFlowVerificationCSVResponse = Blob
export type GetCashFlowVerificationCSVRequest = {
  mismatchOnly?: boolean
}

export type GetTopAccountsRequest = {
  filters?: AccountFiltersEnum[]
  limit?: number
  period?: PeriodRangeDate
}

export type GetTopAccountsResponse = ServerResponse<AccountType[]>

export type SetExcludedFromTopRequest = {
  accountId: number
  excludedFromTop: boolean
}

export type SetExcludedFromTopResponse = ServerResponse<null>

export type SetCfdStatusRequest = {
  accountId: number
  isCfd: boolean
}

export type SetCfdStatusResponse = ServerResponse<UserAccount>

export type AdminCashFlowsRequest = {
  page: number
  perPage: number
  period: (string | null)[]
  account: number
  order: 'ASC' | 'DESC'
  orderBy: keyof CashFlowEntry | null
}

export type AdminCashFlowsData = {
  data: RawCashFlowEntry[]
  meta: PagePaginationFromKeys<keyof CashFlowEntry>
}

export type AdminCashFlowsResponse = ServerResponse<AdminCashFlowsData>

export type AdminCashFlowBalanceRequest = {
  account: number
}

export type AdminCashFlowBalanceResponse = ServerResponse<{ balance: number }>

export type AdminCashFlowStatsRequest = {
  account: number
  period: (string | null)[]
}

export type AdminCashFlowStatsResponse = ServerResponse<CashFlowStatsResponseType>
